import { ChangeDetectionStrategy, Component, computed, effect, inject, OnDestroy, OnInit, Signal, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TagModule } from 'primeng/tag';
import { TranslocoModule } from '@jsverse/transloco';
import { TooltipModule } from 'primeng/tooltip';
import { Subject, takeUntil } from 'rxjs';
import { IconRoute, routes } from '../../../../app.routes';
import { UnderConstructionComponent } from '../../../pages/under-construction/under-construction.component';
import { AppConfigService } from '../../../../services/app-config.service';
import { AuthService } from '../../../../services/auth.service';
import { PermissionService } from '../../../../services/permission.service';
import { UserImpersonationService } from '../../../../services/user-impersonation.service';
import { AssetGroupStore } from '../../../../stores/asset-group.store';
import { AssetStore } from '../../../../stores/asset.store';
import { UserStore } from '../../../../stores/user.store';
import { AssetGroup } from '../../../../types/asset-group.interface';
import { Asset } from '../../../../types/asset.interface';
import { RolePermissionType } from '../../../../types/role.interface';
import { normalizeUrl } from '../../../../utils/strings';
import { RouteUtils } from '../../../../utils/route.utils';
import { CesButtonSecondaryComponent, CesFavoriteButtonComponent } from '../../ces-buttons';
import {
  GenericSidebarComponent,
  SidebarFooterDirective,
  SidebarHeaderDirective,
  SidebarSection,
} from '../generic-sidebar/generic-sidebar.component';

@Component({
  selector: 'app-asset-sidebar',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'relative block h-full' },
  imports: [
    TagModule,
    GenericSidebarComponent,
    SidebarHeaderDirective,
    SidebarFooterDirective,
    TooltipModule,
    CesButtonSecondaryComponent,
    CesFavoriteButtonComponent,
    TranslocoModule,
  ],
  templateUrl: './asset-sidebar.component.html',
})
export class AssetSidebarComponent implements OnInit, OnDestroy {
  private readonly assetStore = inject(AssetStore);
  private readonly assetGroupStore = inject(AssetGroupStore);
  private readonly userStore = inject(UserStore);
  private readonly route = inject(ActivatedRoute);
  private readonly configService = inject(AppConfigService);
  private readonly permissionService = inject(PermissionService);
  private readonly authService = inject(AuthService);
  private readonly impersonationService = inject(UserImpersonationService);
  private readonly APP_VERSION_CONFIG_KEY = 'version';

  protected readonly router = inject(Router);
  private readonly destroy$ = new Subject<void>();
  private readonly currentAssetId = signal<number | null>(null);

  get documentationUrl() {
    return this.currentAsset()?.documentationUrl;
  }

  readonly isCollapsed = signal<boolean>(this.configService.get<boolean>(GenericSidebarComponent.CONFIG_KEY, false));
  readonly appVersion = signal<string>(this.configService.get<string>(this.APP_VERSION_CONFIG_KEY, '0.0.0'));
  readonly menuSections = signal<SidebarSection[]>([]);
  readonly filteredMenuSections = signal<SidebarSection[]>([]);

  readonly currentAsset: Signal<Asset | undefined> = computed(() => {
    const id = this.currentAssetId();
    if (!id) return undefined;
    return this.assetStore.entities().find((asset: Asset) => asset.id === id);
  });

  readonly currentAssetGroup: Signal<AssetGroup | null> = computed(() => {
    const asset = this.currentAsset();
    if (asset?.assetGroupId) {
      return this.assetGroupStore.byId()(asset.assetGroupId) ?? null;
    }
    return null;
  });

  constructor() {
    // Filter menu sections by user permissions
    effect(() => {
      const sections = this.menuSections();
      const assetId = this.currentAssetId();

      if (!sections || sections.length === 0 || !assetId) {
        this.filteredMenuSections.set(sections || []);
        return;
      }

      void this.filterMenuSectionsByPermissions(sections, assetId);
    });
  }

  updateCollapsedState(value: boolean): void {
    this.isCollapsed.set(value);
  }

  ngOnInit(): void {
    this.extractRoutes();
    this.setupRouteListener();

    const userId = this.authService.currentUserId();
    const asset_id = this.currentAssetId();
    if (userId && asset_id) {
      void this.userStore.updateUserAssetSetting(userId, asset_id);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupRouteListener(): void {
    this.route.parent?.paramMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
      const id = params.get('id');
      if (id) {
        const numericId = Number(id);
        if (numericId !== this.currentAssetId()) {
          this.currentAssetId.set(numericId);
          void this.loadAssetIfNeeded(numericId);
        }
      }
    });
  }

  private async loadAssetIfNeeded(id: number): Promise<void> {
    if (!this.assetStore.byId()(id)) {
      try {
        const asset = await this.assetStore.loadById(id);
        if (asset?.assetGroupId) {
          await this.assetGroupStore.loadById(asset.assetGroupId);
        }
      } catch (error) {
        console.error('Error loading asset or asset group:', error);
      }
    }
  }

  private extractRoutes(): void {
    const assetRoute = routes.find(route => route.path === 'assets');
    if (!assetRoute?.children) return;

    const idRouteWithChildren = assetRoute.children.find(route => route.path === ':id' && route.children && !route.outlet);

    if (!idRouteWithChildren?.children) return;

    this.menuSections.set(RouteUtils.toSidebarSections(idRouteWithChildren.children));
  }

  private async filterMenuSectionsByPermissions(sections: SidebarSection[], assetId: number): Promise<void> {
    const realUserId = this.authService.currentUserId();
    const effectiveUserId = this.impersonationService.getEffectiveUserId(realUserId);

    if (!effectiveUserId) {
      this.filteredMenuSections.set([]);
      return;
    }

    // Resolve every route's permission in parallel and keep the current menu visible until the new
    // one is ready (no interim clear), so the sidebar no longer flashes empty while permissions load.
    const filteredSections = await Promise.all(
      sections.map(async section => {
        const permitted = await Promise.all(
          section.routes.map(route =>
            this.permissionService
              .getPermissionTypeForRoute(effectiveUserId, assetId, `${section.path}/${route.path}`)
              .then(permissionType => permissionType !== RolePermissionType.None),
          ),
        );
        return { ...section, routes: section.routes.filter((_, index) => permitted[index]) };
      }),
    );

    this.filteredMenuSections.set(filteredSections.filter(section => section.routes.length > 0));
  }

  isUnderConstruction = (route: IconRoute): boolean => {
    return route.component === UnderConstructionComponent;
  };

  navigateHome(): void {
    void this.router.navigate(['']);
  }

  openDocumentation(): void {
    if (this.documentationUrl) {
      window.open(normalizeUrl(this.documentationUrl), '_blank', 'noopener');
    }
  }

  async toggleFavorite() {
    const userId = this.authService.currentUserId();
    const currentAsset = this.currentAsset();

    if (!userId || !currentAsset) return;

    const newFavoriteState = !currentAsset.isFavorite;

    await this.assetStore.update(currentAsset.id, { isFavorite: newFavoriteState });

    try {
      await this.userStore.updateUserAssetSetting(userId, currentAsset.id, { favorite: newFavoriteState });
    } catch {
      await this.assetStore.update(currentAsset.id, { isFavorite: !newFavoriteState });
    }
  }
}
